import { useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChevronDown, ChevronRight, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface Registration {
  id: string;
  first_name: string;
  last_name: string;
  region: string;
  category: string | null;
  category_price: number | null;
  conference_fee?: number | null;
  amount_due?: number | null;
  children_0_5?: number | null;
  children_6_12?: number | null;
  teenagers_13_17?: number | null;
  adults_18_plus?: number | null;
  total_members?: number | null;
  spouse_first_name?: string | null;
  spouse_middle_name?: string | null;
  spouse_last_name?: string | null;
  spouse_email?: string | null;
  spouse_mobile_country_code?: string | null;
  spouse_mobile_number?: string | null;
  zelle_memo?: string | null;
}
interface MemberRow {
  id: string;
  registration_id: string;
  first_name: string;
  middle_name?: string | null;
  last_name: string;
  relationship: string; // primary | spouse | child | other_adult
  age_group: string; // adult | teen | child_6_12 | child_0_5
  age?: number | null;
  attendee_type?: string | null;
  child_category?: string | null;
  email?: string | null;
  mobile_country_code?: string | null;
  mobile_number?: string | null;
}

type IssueKey =
  | "child_as_other_adult"
  | "adult_under_18"
  | "child_marked_adult"
  | "possible_spouse"
  | "same_surname_adult"
  | "multiple_spouses"
  | "extra_adult_fee";

interface Issue {
  key: IssueKey;
  label: string;
  suggestion: string;
  memberId?: string;
  feeDelta?: number; // change to apply if fix accepted (negative = refund)
}

interface Props {
  rows: Registration[];
  members: Record<string, MemberRow[]>;
  onRefresh: () => void;
  onOpenRegistration?: (id: string) => void;
}

const isSouthern = (r: Registration) => /southern/i.test(r.region || "");
const ADDITIONAL_ADULT_FEE = 125;

const asNumber = (value: unknown, fallback = 0) => {
  const next = Number(value ?? fallback);
  return Number.isFinite(next) ? next : fallback;
};

const formatFee = (amount: number) => {
  const rounded = Math.round(amount * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
};

const toastErrorMessage = (err: unknown) => {
  if (err instanceof Error && err.message) return err.message;
  if (typeof err === "object" && err !== null && "message" in err) {
    const message = (err as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) return message;
  }
  return "The registration could not be updated. Please refresh and try again.";
};

const deriveCounts = (mem: MemberRow[]) => ({
  children_0_5: mem.filter((m) => m.relationship === "child" && m.age_group === "child_0_5").length,
  children_6_12: mem.filter((m) => m.relationship === "child" && m.age_group === "child_6_12").length,
  teenagers_13_17: mem.filter((m) => m.relationship === "child" && m.age_group === "teen").length,
  adults_18_plus: mem.filter((m) => m.age_group === "adult").length,
  total_members: mem.length,
});

const isAdultDependent = (m: MemberRow) =>
  m.relationship === "child" &&
  (m.child_category === "adult_18_plus" || m.age_group === "adult" || (m.age != null && m.age >= 18));

const childAgeHint = (m: MemberRow) => {
  if (m.age != null) return m.age;
  if (m.age_group === "child_0_5") return 5;
  if (m.age_group === "child_6_12") return 6;
  if (m.age_group === "teen") return 13;
  return 18;
};

const computeExpectedFee = (r: Registration, mem: MemberRow[], fallbackDelta = -ADDITIONAL_ADULT_FEE) => {
  const currentFee = asNumber(r.conference_fee ?? r.category_price);
  if (!isSouthern(r)) return Math.max(0, currentFee + fallbackDelta);

  const category = (r.category || "").toLowerCase();
  const hasSpouse = mem.some((m) => m.relationship === "spouse");
  const otherAdults = mem.filter((m) => m.relationship === "other_adult").length;
  const adultDependents = mem.filter(isAdultDependent).length;
  const additionalAdults = otherAdults + adultDependents;
  const extraAdultFee = additionalAdults * ADDITIONAL_ADULT_FEE;

  if (category === "individual") return ADDITIONAL_ADULT_FEE + extraAdultFee;
  if (category === "couple") return (hasSpouse ? 250 : ADDITIONAL_ADULT_FEE) + extraAdultFee;
  if (category !== "family") return Math.max(0, currentFee + fallbackDelta);

  const minorChildren = mem.filter((m) => m.relationship === "child" && !isAdultDependent(m));
  const olderChildren = minorChildren.filter((m) => childAgeHint(m) > 5).length;
  const familyBase = hasSpouse
    ? olderChildren > 0
      ? 350
      : 250
    : olderChildren >= 2
      ? 350
      : olderChildren === 1
        ? 250
        : ADDITIONAL_ADULT_FEE;

  return familyBase + extraAdultFee;
};

const adjustedDue = (r: Registration, newFee: number) => {
  if (r.amount_due == null) return null;
  const oldFee = asNumber(r.conference_fee ?? r.category_price);
  return Math.max(0, asNumber(r.amount_due) + (newFee - oldFee));
};

const memoWithFee = (memo: string | null | undefined, newFee: number) =>
  (memo || "").replace(/\$\d+(?:\.\d+)?/g, `$${formatFee(newFee)}`);

function detectIssues(r: Registration, mem: MemberRow[]): Issue[] {
  const issues: Issue[] = [];
  const spouse = mem.filter((m) => m.relationship === "spouse");
  const otherAdults = mem.filter((m) => m.relationship === "other_adult");
  const children = mem.filter((m) => m.relationship === "child");
  const primary = mem.find((m) => m.relationship === "primary");
  const primaryLast = (r.last_name || primary?.last_name || "").trim().toLowerCase();

  if (spouse.length > 1) {
    issues.push({ key: "multiple_spouses", label: "More than one spouse recorded", suggestion: "Remove duplicate spouse rows." });
  }

  otherAdults.forEach((a) => {
    if (a.age != null && a.age < 18) {
      issues.push({
        key: "child_as_other_adult",
        label: `${a.first_name} ${a.last_name} (age ${a.age}) is under 18 but stored as Other Adult`,
        suggestion: "Reclassify to Child and remove the $125 additional-adult fee.",
        memberId: a.id,
        feeDelta: -125,
      });
    } else if (a.age_group !== "adult") {
      issues.push({
        key: "child_as_other_adult",
        label: `${a.first_name} ${a.last_name} is stored as Other Adult but age group is ${a.age_group}`,
        suggestion: "Reclassify to the correct child/teen category and remove the $125 additional-adult fee.",
        memberId: a.id,
        feeDelta: -125,
      });
    }
  });

  children.forEach((c) => {
    if (c.age_group === "adult" || (c.age != null && c.age >= 18)) {
      issues.push({
        key: "child_marked_adult",
        label: `${c.first_name} ${c.last_name} is marked as Child but age group is Adult${c.age != null ? ` (age ${c.age})` : ""}`,
        suggestion: "Move to Other Adult (may add $125) or correct the age.",
        memberId: c.id,
      });
    }
  });

  // Possible-spouse: no spouse row + exactly one Other Adult with same last name.
  if (spouse.length === 0 && otherAdults.length === 1) {
    const a = otherAdults[0];
    const sameLast = !!primaryLast && (a.last_name || "").trim().toLowerCase() === primaryLast;
    issues.push({
      key: "possible_spouse",
      label: `${a.first_name} ${a.last_name} may be the spouse (no spouse recorded${sameLast ? ", shares your last name" : ""})`,
      suggestion: "Move to Spouse and remove the $125 additional-adult fee.",
      memberId: a.id,
      feeDelta: -125,
    });
  }

  // Same surname Other Adult while spouse also present
  if (spouse.length >= 1) {
    otherAdults.forEach((a) => {
      const sameLast = !!primaryLast && (a.last_name || "").trim().toLowerCase() === primaryLast;
      if (sameLast) {
        issues.push({
          key: "same_surname_adult",
          label: `${a.first_name} ${a.last_name} shares your last name — verify this is not a family member`,
          suggestion: "Confirm they should be charged $125 as an Additional Adult, or reclassify.",
          memberId: a.id,
        });
      }
    });
  }

  // Informational: any Other Adult increases fee by $125
  if (otherAdults.length >= 1 && isSouthern(r)) {
    issues.push({
      key: "extra_adult_fee",
      label: `${otherAdults.length} Additional Adult${otherAdults.length === 1 ? "" : "s"} — adds $${125 * otherAdults.length} to the fee`,
      suggestion: "Verify each is a true Additional Adult (not spouse or child).",
    });
  }

  return issues;
}

export const RegistrationIssuesPanel = ({ rows, members, onRefresh, onOpenRegistration }: Props) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);
  const [showInfo, setShowInfo] = useState(false);

  const flagged = useMemo(() => {
    const list: { r: Registration; issues: Issue[] }[] = [];
    rows.forEach((r) => {
      const mem = members[r.id] || [];
      const all = detectIssues(r, mem);
      const filtered = showInfo ? all : all.filter((i) => i.key !== "extra_adult_fee" && i.key !== "same_surname_adult");
      if (filtered.length) list.push({ r, issues: filtered });
    });
    return list.sort((a, b) => b.issues.length - a.issues.length);
  }, [rows, members, showInfo]);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const applyMoveToSpouse = async (r: Registration, adult: MemberRow) => {
    setBusy(r.id);
    try {
      const nextMembers = (members[r.id] || []).map((m) =>
        m.id === adult.id
          ? { ...m, relationship: "spouse", age_group: "adult", attendee_type: "adult", child_category: null }
          : m,
      );
      const newFee = computeExpectedFee(r, nextMembers);
      const newDue = adjustedDue(r, newFee);
      const counts = deriveCounts(nextMembers);

      // 1) Update the member row to spouse
      const { error: mErr } = await (supabase as any)
        .from("registration_members")
        .update({ relationship: "spouse", age_group: "adult", attendee_type: "adult", child_category: null })
        .eq("id", adult.id);
      if (mErr) throw mErr;

      // 2) Update the parent registration: spouse fields, totals, memo
      const { error: rErr } = await (supabase as any)
        .from("conference_registrations")
        .update({
          spouse_first_name: adult.first_name,
          spouse_last_name: adult.last_name,
          spouse_middle_name: adult.middle_name ?? null,
          spouse_email: adult.email ?? null,
          spouse_mobile_country_code: adult.mobile_country_code ?? null,
          spouse_mobile_number: adult.mobile_number ?? null,
          category_price: newFee,
          conference_fee: newFee,
          amount_due: newDue,
          ...counts,
          zelle_memo: memoWithFee(r.zelle_memo, newFee),
        })
        .eq("id", r.id);
      if (rErr) throw rErr;

      toast({ title: "Moved to Spouse", description: `Fee updated to $${formatFee(newFee)}.` });
      onRefresh();
    } catch (err: unknown) {
      toast({ title: "Fix failed", description: toastErrorMessage(err), variant: "destructive" });
    } finally {
      setBusy(null);
    }
  };

  const applyReclassifyChild = async (r: Registration, adult: MemberRow) => {
    setBusy(r.id);
    try {
      // Reclassify Other Adult row to child based on age
      const age = adult.age ?? 12;
      const ageGroup = age <= 5 ? "child_0_5" : age <= 12 ? "child_6_12" : age <= 17 ? "teen" : "adult";
      const childCategory = age <= 5 ? "child_0_5" : age <= 12 ? "child_6_12" : age <= 17 ? "teen" : "adult_18_plus";
      const nextMembers = (members[r.id] || []).map((m) =>
        m.id === adult.id
          ? { ...m, relationship: "child", age_group: ageGroup, child_category: childCategory, attendee_type: "child" }
          : m,
      );
      const newFee = computeExpectedFee(r, nextMembers);
      const newDue = adjustedDue(r, newFee);
      const counts = deriveCounts(nextMembers);

      const { error: mErr } = await (supabase as any)
        .from("registration_members")
        .update({ relationship: "child", age_group: ageGroup, child_category: childCategory, attendee_type: "child" })
        .eq("id", adult.id);
      if (mErr) throw mErr;

      const { error: rErr } = await (supabase as any)
        .from("conference_registrations")
        .update({ category_price: newFee, conference_fee: newFee, amount_due: newDue, ...counts, zelle_memo: memoWithFee(r.zelle_memo, newFee) })
        .eq("id", r.id);
      if (rErr) throw rErr;
      toast({ title: "Reclassified to child", description: `Fee updated to $${formatFee(newFee)}.` });
      onRefresh();
    } catch (err: unknown) {
      toast({ title: "Fix failed", description: toastErrorMessage(err), variant: "destructive" });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card className="mb-6 border-amber-200">
      <CardHeader className="flex flex-row items-center justify-between gap-4 flex-wrap">
        <div>
          <CardTitle className="flex items-center gap-2 text-amber-800">
            <AlertTriangle className="w-5 h-5" /> Potential Registration Issues
            <Badge variant="secondary">{flagged.length}</Badge>
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Flags registrations where a family member may be misclassified — the most common cause of an extra $125 charge.
          </p>
        </div>
        <label className="text-xs text-slate-600 flex items-center gap-2 cursor-pointer">
          <input type="checkbox" className="w-4 h-4" checked={showInfo} onChange={(e) => setShowInfo(e.target.checked)} />
          Show informational flags (Additional Adult fee, same surname)
        </label>
      </CardHeader>
      <CardContent>
        {flagged.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">No issues detected.</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>Primary</TableHead>
                  <TableHead>Region</TableHead>
                  <TableHead>Issues</TableHead>
                  <TableHead className="text-right">Current Fee</TableHead>
                  <TableHead className="text-right">Expected Fee</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {flagged.map(({ r, issues }) => {
                  const current = r.category_price ?? 0;
                  const feeDelta = issues.reduce((s, i) => s + (i.feeDelta || 0), 0);
                  const expected = Math.max(0, current + feeDelta);
                  const isOpen = expanded.has(r.id);
                  return (
                    <>
                      <TableRow key={r.id} className="align-top">
                        <TableCell>
                          <Button variant="ghost" size="icon" onClick={() => toggle(r.id)}>
                            {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </Button>
                        </TableCell>
                        <TableCell className="font-medium">
                          {r.first_name} {r.last_name}
                          <div className="text-[11px] text-slate-500 font-mono">{r.id.slice(0, 8)}</div>
                        </TableCell>
                        <TableCell>{r.region}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {issues.slice(0, 3).map((i, idx) => (
                              <Badge key={idx} variant="outline" className="text-[10px]">
                                {i.key.replace(/_/g, " ")}
                              </Badge>
                            ))}
                            {issues.length > 3 && <Badge variant="secondary" className="text-[10px]">+{issues.length - 3}</Badge>}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">${current}</TableCell>
                        <TableCell className={`text-right ${feeDelta ? "text-emerald-700 font-semibold" : ""}`}>
                          {feeDelta ? `$${expected}` : "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          {onOpenRegistration && (
                            <Button variant="outline" size="sm" onClick={() => onOpenRegistration(r.id)}>
                              Open
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                      {isOpen && (
                        <TableRow key={r.id + "-details"}>
                          <TableCell />
                          <TableCell colSpan={6}>
                            <div className="space-y-2 py-2">
                              {issues.map((i, idx) => {
                                const mem = (members[r.id] || []).find((m) => m.id === i.memberId);
                                return (
                                  <div key={idx} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-100 bg-amber-50/60 px-3 py-2 text-xs">
                                    <div>
                                      <div className="font-semibold text-slate-800">{i.label}</div>
                                      <div className="text-slate-600">{i.suggestion}</div>
                                    </div>
                                    <div className="flex gap-2">
                                      {i.key === "possible_spouse" && mem && (
                                        <Button size="sm" disabled={busy === r.id} onClick={() => applyMoveToSpouse(r, mem)}>
                                          {busy === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Move to Spouse"}
                                        </Button>
                                      )}
                                      {i.key === "child_as_other_adult" && mem && (
                                        <Button size="sm" disabled={busy === r.id} onClick={() => applyReclassifyChild(r, mem)}>
                                          {busy === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Reclassify as Child"}
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RegistrationIssuesPanel;
